import { useEffect, useRef, useState } from "react";
import { MisEntry } from "../../interfaces/ActionStartResponse";
import {
    ALL_SOURCES_ID,
    ContactDTO,
    CreateContact,
    CreateContactRequest,
    GetSources,
    SourceDTO,
    UpdateContact,
} from "../../Logic/EmsAPI/Contacts";
import { IsEmailAddress } from "../../utilities/string";
import Confirm from "../Generic/ConfirmDialog";
import Checkbox from "../Generic/Form/Checkbox";
import Dropdown from "../Generic/Form/DropDown";
import Email from "../Generic/Form/Email";
import Numeric from "../Generic/Form/Numeric";
import TextBox from "../Generic/Form/TextBox";

// tblContacts.contact_name and email_address are varchar(128)
const MAX_LENGTH = 128;
const MIN_PRIORITY = 1;
const MAX_PRIORITY = 255;

interface ContactForm {
    name: string;
    emailAddress: string;
    sourceID: string;
    priority: string;
    autoRespond: boolean;
    htmlEnabled: boolean;
    spam: boolean;
}

// auto respond and HTML start ticked: that is what inbound mail assumes for a sender with no contact
const emptyForm: ContactForm = {
    name: "",
    emailAddress: "",
    sourceID: "",
    priority: "",
    autoRespond: true,
    htmlEnabled: true,
    spam: false,
};

// legacy contacts can have no name, source or priority; those fields start empty and must be filled in
function toForm(contact: ContactDTO): ContactForm {
    return {
        name: contact.name ?? "",
        emailAddress: contact.emailAddress ?? "",
        sourceID: contact.sourceID?.toString() ?? "",
        priority: contact.priority?.toString() ?? "",
        autoRespond: contact.autoRespond,
        htmlEnabled: contact.htmlEnabled,
        spam: contact.spam,
    };
}

function validate(form: ContactForm) {
    const priority = Number(form.priority);
    return {
        name: form.name.trim() !== "",
        emailAddress: form.emailAddress.trim() !== "" && IsEmailAddress(form.emailAddress.trim()),
        sourceID: form.sourceID !== "" && form.sourceID !== ALL_SOURCES_ID.toString(),
        priority: /^\d+$/.test(form.priority.trim()) && priority >= MIN_PRIORITY && priority <= MAX_PRIORITY,
    };
}

function toRequest(form: ContactForm): CreateContactRequest {
    return {
        name: form.name.trim(),
        emailAddress: form.emailAddress.trim(),
        sourceID: Number(form.sourceID),
        priority: Number(form.priority),
        autoRespond: form.autoRespond,
        htmlEnabled: form.htmlEnabled,
        spam: form.spam,
    };
}

interface Props {
    // the contact to edit; leave it out to add a new one
    contact?: ContactDTO;
    onClose: () => void;
    onSaved: (id: number) => void;
}

function ContactModal(props: Props) {
    const editing = props.contact !== undefined;
    const [form, setForm] = useState<ContactForm>(() => (props.contact ? toForm(props.contact) : emptyForm));
    const [sources, setSources] = useState<SourceDTO[]>([]);
    const [showErrors, setShowErrors] = useState(false);
    const [serverErrors, setServerErrors] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        GetSources().then(response => {
            if (response.isOk && response.data) {
                setSources(
                    response.data.items
                        .filter(x => x.id !== ALL_SOURCES_ID)
                        .sort((a, b) => a.name.localeCompare(b.name)),
                );
            }
        });
    }, []);

    const valid = validate(form);
    const isValid = Object.values(valid).every(x => x);

    const update = (changes: Partial<ContactForm>) => setForm({ ...form, ...changes });

    const submit = async () => {
        if (saving) return;
        setShowErrors(true);
        setServerErrors([]);
        if (!isValid) return;

        setSaving(true);
        const request = toRequest(form);
        const response = props.contact ? await UpdateContact(props.contact.id, request) : await CreateContact(request);
        setSaving(false);

        // an update answers 204 with no body, so its ID is the one being edited
        const savedID = props.contact ? props.contact.id : response.data?.id;
        if (response.isOk && savedID !== undefined) {
            props.onSaved(savedID);
            props.onClose();
        } else if (response.validationErrors?.errors) {
            setServerErrors(Object.values(response.validationErrors.errors).flat());
        }
    };

    // Confirm wires up its Enter key once, so it must call through to the latest submit
    const submitRef = useRef(submit);
    submitRef.current = submit;

    const checkbox = (id: number, label: string, checked: boolean, onChange: (checked: boolean) => void) => (
        <Checkbox
            MisField={{ id, name: `contact-${id}`, label, displayOrder: 0, fieldType: "Checkbox" }}
            MisEntry={{ fieldID: id, value: checked ? "1" : "0" }}
            returnFunc={(entry: MisEntry) => onChange(entry.value === "1")}
            Valid={true}
        />
    );

    return (
        <Confirm
            title={editing ? "Edit Contact" : "Add Contact"}
            oKText={editing ? "Save" : "Add Contact"}
            onConfirm={() => submitRef.current()}
            onCancel={props.onClose}
            isValid={isValid && !saving}
        >
            <div style={{ width: "450px" }}>
                <TextBox
                    id="contact-name"
                    label="Name"
                    value={form.name}
                    onChange={value => update({ name: value })}
                    maxLength={MAX_LENGTH}
                    requiredIdentifier={true}
                    isNotValid={showErrors && !valid.name}
                    setFocus={true}
                />
                <Email
                    id="contact-email"
                    label="Email Address"
                    value={form.emailAddress}
                    onChange={value => update({ emailAddress: value })}
                    maxLength={MAX_LENGTH}
                    requiredIdentifier={true}
                    isNotValid={showErrors && !valid.emailAddress}
                />
                <Dropdown
                    MisField={{
                        id: 9001,
                        name: "contact-source",
                        label: "Source",
                        displayOrder: 0,
                        fieldType: "Dropdown",
                        lookupValues: sources.map((x, i) => ({ id: x.id, name: x.name, order: i })),
                    }}
                    MisEntry={{ fieldID: 9001, value: form.sourceID }}
                    returnFunc={(entry: MisEntry) => update({ sourceID: entry.value })}
                    Valid={!showErrors || valid.sourceID}
                    excludeQuickFind={true}
                />
                <Numeric
                    id="contact-priority"
                    label="Priority"
                    value={form.priority}
                    onChange={value => update({ priority: value })}
                    min={MIN_PRIORITY}
                    max={MAX_PRIORITY}
                    requiredIdentifier={true}
                    isNotValid={showErrors && !valid.priority}
                />
                <div className="row">
                    <div className="col-sm-9 offset-sm-3">
                        <small>1 = Highest priority, 255 = Lowest priority</small>
                    </div>
                </div>
                {checkbox(9002, "Spam", form.spam, checked => update({ spam: checked }))}
                {checkbox(9003, "Auto Respond", form.autoRespond, checked => update({ autoRespond: checked }))}
                {checkbox(9004, "HTML Enabled", form.htmlEnabled, checked => update({ htmlEnabled: checked }))}
                {serverErrors.length > 0 && (
                    <div
                        className="validationFailedMessage"
                        style={{ color: "red" }}
                    >
                        {serverErrors.map(message => (
                            <div key={message}>{message}</div>
                        ))}
                    </div>
                )}
            </div>
        </Confirm>
    );
}
export default ContactModal;
